import { caStateCodes } from '@modules/internationalisation/state';
import { promoWithCatalogInformationSchema } from '@modules/promotions/v2/schema';
import { z } from 'zod';
import { isProductKey } from '../productCatalog';
/**
 * This file is used to validate data that gets injected from
 * the Play controllers onto the `window.guardian` object of the page.
 *
 * It will only error in NODE_ENV === 'development'.
 */
const featureSwitchesSchema = z.object({
    enableQuantumMetric: z.optional(z.enum(['On', 'Off'])),
    usStripeAccountForSingle: z.optional(z.enum(['On', 'Off'])),
    authenticateWithOkta: z.optional(z.enum(['On', 'Off'])),
    enableCampaignCountdown: z.optional(z.enum(['On', 'Off'])),
    enableThankYouOnboarding: z.optional(z.enum(['On', 'Off'])),
    enableCheckoutNudge: z.optional(z.enum(['On', 'Off'])),
    enableMParticle: z.optional(z.enum(['On', 'Off'])),
    enableDeepDiscount: z.optional(z.enum(['On', 'Off'])),
    enableStudentBeansEuropeAustralia: z.optional(z.enum(['On', 'Off'])),
});
const PaymentConfigSchema = z.object({
    geoip: z.optional(z.object({
        countryCode: z.string(),
        stateCode: z.optional(z.string()),
    })),
    stripeKeyDefaultCurrencies: z.object({
        ONE_OFF: z.object({ default: z.string(), test: z.string() }),
        REGULAR: z.object({ default: z.string(), test: z.string() }),
    }),
    stripeKeyAustralia: z.object({
        ONE_OFF: z.object({ default: z.string(), test: z.string() }),
        REGULAR: z.object({ default: z.string(), test: z.string() }),
    }),
    stripeKeyUnitedStates: z.object({
        ONE_OFF: z.object({ default: z.string(), test: z.string() }),
        REGULAR: z.object({ default: z.string(), test: z.string() }),
    }),
    stripeKeyTortoiseMedia: z.object({
        ONE_OFF: z.object({ default: z.string(), test: z.string() }),
        REGULAR: z.object({ default: z.string(), test: z.string() }),
    }),
    payPalClientId: z.object({
        default: z.string(),
        test: z.string(),
    }),
    mdapiUrl: z.string(),
    paymentApiPayPalEndpoint: z.string(),
    paymentApiUrl: z.string(),
    csrf: z.object({ token: z.string() }),
    guestAccountCreationToken: z.optional(z.string()),
    recaptchaEnabled: z.boolean(),
    v2recaptchaPublicKey: z.string(),
    user: z.optional(z.object({
        id: z.string(),
        email: z.optional(z.string()),
        firstName: z.optional(z.string()),
        lastName: z.optional(z.string()),
    })),
    serversideTests: z.optional(z.object({})),
    settings: z.object({
        /**
         * These keys are generated in Switches.scala
         * @see {@link file://./../../../app/admin/settings/Switches.scala}
         *
         * And added to the `window.guardian` object in settingsScript.scala.html
         * @see {@link file://./../../../app/views/settingsScript.scala.html}
         */
        switches: z.object({
            oneOffPaymentMethods: z.record(z.string(), z.optional(z.enum(['On', 'Off']))),
            recurringPaymentMethods: z.record(z.string(), z.optional(z.enum(['On', 'Off']))),
            subscriptionsSwitches: z.record(z.string(), z.optional(z.enum(['On', 'Off']))),
            featureSwitches: featureSwitchesSchema,
            campaignSwitches: z.record(z.string(), z.optional(z.enum(['On', 'Off']))),
            recaptchaSwitches: z.record(z.string(), z.optional(z.enum(['On', 'Off']))),
        }),
        contributionTypes: z.object({
            AUDCountries: z.array(z.object({
                contributionType: z.enum(['ONE_OFF', 'MONTHLY', 'ANNUAL']),
                isDefault: z.optional(z.boolean()),
            })),
            Canada: z.array(z.object({
                contributionType: z.enum(['ONE_OFF', 'MONTHLY', 'ANNUAL']),
                isDefault: z.optional(z.boolean()),
            })),
            EURCountries: z.array(z.object({
                contributionType: z.enum(['ONE_OFF', 'MONTHLY', 'ANNUAL']),
                isDefault: z.optional(z.boolean()),
            })),
            GBPCountries: z.array(z.object({
                contributionType: z.enum(['ONE_OFF', 'MONTHLY', 'ANNUAL']),
                isDefault: z.optional(z.boolean()),
            })),
            International: z.array(z.object({
                contributionType: z.enum(['ONE_OFF', 'MONTHLY', 'ANNUAL']),
                isDefault: z.optional(z.boolean()),
            })),
            NZDCountries: z.array(z.object({
                contributionType: z.enum(['ONE_OFF', 'MONTHLY', 'ANNUAL']),
                isDefault: z.optional(z.boolean()),
            })),
            UnitedStates: z.array(z.object({
                contributionType: z.enum(['ONE_OFF', 'MONTHLY', 'ANNUAL']),
                isDefault: z.optional(z.boolean()),
            })),
        }),
        metricUrl: z.string(),
        productsWithThankYouOnboarding: z.array(z.string().refine(isProductKey)),
    }),
    isObserverSubdomain: z.boolean(),
});
const ProductCatalogSchema = z.object({
    productCatalog: z.record(z.string(), z.object({
        ratePlans: z.record(z.string(), z.object({
            id: z.string(),
            pricing: z.record(z.string(), z.number()),
            charges: z.record(z.string(), z.object({
                id: z.string(),
            })),
            billingPeriod: z.optional(z.enum(['Quarter', 'Month', 'Annual', 'OneTime'])),
            savingVsRetail: z.number().nullish(),
            taxMode: z.enum(['TaxExclusive', 'TaxInclusive']).nullable(),
        })),
    })),
});
export const dateTimeSchema = z.preprocess((val) => (typeof val === 'string' ? new Date(val) : val), z.date());
const TaxRatesSchema = z.object({
    taxRates: z
        .partialRecord(z.enum(['SupporterPlus', 'DigitalSubscription']), z.record(z.enum(caStateCodes), z.number()))
        .optional(), // This isn't made available on every page
});
const PromotionsSchema = z.object({
    promotions: z.array(promoWithCatalogInformationSchema).optional(), // This isn't made available on every page
    checkoutNudgePromotions: z
        .array(promoWithCatalogInformationSchema)
        .optional(), // Only made available on checkout pages
});
const AppConfigSchema = PaymentConfigSchema.merge(ProductCatalogSchema)
    .merge(TaxRatesSchema)
    .merge(PromotionsSchema);
export const parseAppConfig = (obj) => {
    const appConfig = AppConfigSchema.safeParse(obj);
    if (appConfig.success) {
        return appConfig.data;
    }
    else {
        // We allow parsing errors through on PROD as they might not be breaking changes
        // but we should be aware of them.
        if (process.env.NODE_ENV === 'development') {
            console.error(appConfig.error);
            throw new SyntaxError('Failed to parse window.guardian value with issues: ', { cause: appConfig.error });
        }
        else {
            console.error('Failed to parse window.guardian value with issues: ', appConfig.error);
        }
    }
    return obj;
};
